import React, { useEffect, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import {
  Eye,
  Edit2,
  Download,
  ChevronDown,
  Plus,
  Save,
  RotateCcw,
  CircleDollarSign,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";
import Modal from "../../../components/Modal/Modal";
import Notification from "../../../components/Notification/Notification";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../apiService";

/* ===================== UTILS ===================== */
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./AdminPricing.module.scss";

const AdminPricing = () => {
  // Granted actions on the "pricing" resource (from /modules/sidebar)
  const canDo = (action) => can("pricing", action);

  const [openCategory, setOpenCategory] = useState("payment");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewOrgId, setViewOrgId] = useState(null);

  /* ===================== NEW STATES (API ONLY) ===================== */
  const [orgList, setOrgList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [categoryData, setCategoryData] = useState([]);
  const [orgError, setOrgError] = useState("");
  const [masterCategories, setMasterCategories] = useState([]);

  /* ===================== TAB ===================== */
  const [activeTab, setActiveTab] = useState("client");

  /* ===================== TABLE ===================== */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enterpriseLoading, setEnterpriseLoading] = useState(false);

  /* ===================== PAGINATION ===================== */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  /* ===================== SEARCH ===================== */
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [showCustomPricing, setShowCustomPricing] = useState(false);
  const [pricingMode, setPricingMode] = useState("add"); // add | view | edit
  const [, setViewLoading] = useState(false);
  const isViewMode = pricingMode === "view";

  const mapViewResponseToForm = (data) => {
    const { master, categories } = data;
    const orgId = master[0]?.org_id;
    setViewOrgId(orgId);
    setGlobalDiscount(Number(master[0]?.discount || 0));

    setCategoryData(
      categories.map((cat) => ({
        categoryId: cat.categoryId,
        categoryName: cat.categoryName,
        categoryDiscount: Number(cat.category_discount || 0),
        apis: cat.apis.map((api) => ({
          apiMasterId: api.id,
          name: api.name,
          default: Number(api.default || 0),
          customize: Number(api.customize || 0),
        })),
      })),
    );
  };

  const getSaveButtonText = () => {
    if (isSubmitting) return "Saving...";
    if (pricingMode === "edit") return "Update Pricing";
    return "Save Pricing";
  };

  const handleViewPricing = async (orgId) => {
    setPricingMode("view");
    setOrgError("");
    setShowCustomPricing(true);
    setViewLoading(true);

    try {
      const res = await apiGet("catalog", `/pricing/client/view/${orgId}`);
      if (res?.status === "success") {
        mapViewResponseToForm(res.data);
      }
    } catch {
      showNotification("Failed to load pricing", "error");
    } finally {
      setViewLoading(false);
    }
  };

  const handleEditPricing = async (orgId) => {
    setPricingMode("edit");
    setOrgError("");
    setShowCustomPricing(true);
    setViewLoading(true);

    try {
      const res = await apiGet("catalog", `/pricing/client/view/${orgId}`);
      if (res?.status === "success") {
        mapViewResponseToForm(res.data);
      }
    } catch {
      showNotification("Failed to load pricing", "error");
    } finally {
      setViewLoading(false);
    }
  };

  const openAddPricing = () => {
    resetCustomPricingForm();
    setOrgError("");
    setPricingMode("add");
    setShowCustomPricing(true);
  };

  const handleCategoryDiscountChange = (categoryId, value) => {
    setCategoryData((prev) =>
      prev.map((cat) => {
        if (cat.categoryId !== categoryId) return cat;

        return {
          ...cat,
          manual: true,
          categoryDiscount: value,
          apis: cat.apis.map((api) => {
            if (api.customized) return api;
            const discounted = api.default - (api.default * value) / 100;
            return {
              ...api,
              customize: value === "" ? "" : Number(discounted.toFixed(2)),
            };
          }),
        };
      }),
    );
  };

  const safeNumber = (value) => {
    if (value === "") return "";
    return Number(value);
  };

  const handleApiCustomizeChange = (categoryId, apiMasterId, value) => {
    setCategoryData((prev) =>
      prev.map((cat) =>
        cat.categoryId === categoryId
          ? {
              ...cat,
              apis: cat.apis.map((api) =>
                api.apiMasterId === apiMasterId
                  ? {
                      ...api,
                      customize: value,
                      customized: true,
                    }
                  : api,
              ),
            }
          : cat,
      ),
    );
  };

  const handleGlobalDiscountChange = (value) => {
    setGlobalDiscount(value);
    setCategoryData((prev) =>
      prev.map((cat) => {
        if (cat.manual) return cat;
        return {
          ...cat,
          categoryDiscount: value,
          apis: cat.apis.map((api) => {
            if (api.customized) return api;
            const discounted = api.default - (api.default * value) / 100;
            return {
              ...api,
              customize: value === "" ? "" : Number(discounted.toFixed(2)),
            };
          }),
        };
      }),
    );
  };

  const resetFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setPage(1);
  };

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* ===================== COLUMNS ===================== */
  const clientColumns = [
    { label: "Company Name", key: "company" },
    { label: "Date & Time", key: "datetime" },
    { label: "Discount (%)", key: "discount" },
    { label: "Type", key: "type" },
    { label: "Actions", key: "action" },
  ];

  const enterpriseColumns = [
    { label: "Full Name", key: "name" },
    { label: "Email", key: "Email" },
    { label: "Mobile", key: "Mobile" },
    { label: "API Name", key: "api" },
    { label: "Monthly Usage", key: "usage" },
  ];

  /* ===================== ENTERPRISE ===================== */
  const [enterpriseRows, setEnterpriseRows] = useState([]);
  const [enterprisePage, setEnterprisePage] = useState(1);
  const [enterpriseLimit, setEnterpriseLimit] = useState(10);
  const [enterprisePagination, setEnterprisePagination] = useState({
    total: 0,
    totalPages: 1,
  });

  const fetchEnterprisePricing = async () => {
    if (activeTab !== "enterprise") return;
    setEnterpriseLoading(true);

    try {
      const query = new URLSearchParams({
        page: enterprisePage,
        limit: enterpriseLimit,
        ...(appliedSearch && { search: appliedSearch }),
      }).toString();

      const res = await apiGet("catalog", `/custom/pricing?${query}`);

      if (res?.status === "success") {
        const mapped =
          res.data.priceData?.map((item) => ({
            name: item.full_name,
            Email: item.email,
            Mobile: item.mobile,
            api: item.name,
            usage: item.monthly_usage,
          })) || [];

        setEnterpriseRows(mapped);
        setEnterprisePagination({
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
        });
      }
    } catch (err) {
      showNotification("Failed to load enterprise pricing", "error");
    } finally {
      setEnterpriseLoading(false);
    }
  };

  /* ===================== API CALL ===================== */
  const fetchPricingData = async () => {
    if (activeTab !== "client") return;
    setLoading(true);

    try {
      const query = new URLSearchParams({
        page,
        limit,
        ...(appliedSearch && { search: appliedSearch }),
      }).toString();

      const res = await apiGet("catalog", `/pricing/client?${query}`);

      if (res?.status === "success") {
        const mappedRows =
          res.data.priceData?.map((item) => ({
            org_id: item.org_id,
            company: item.company_name || "—",
            datetime: item.created_at,
            discount: `${item.discount}`,
            type: item.api_type,
          })) || [];

        setRows(mappedRows);
        setPagination({
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error("Pricing API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    let headers = "";
    let rowsData = "";
    let fileName = "";

    if (activeTab === "client") {
      headers =
        clientColumns
          .filter((col) => col.key !== "action")
          .map((col) => col.label)
          .join(",") + "\n";

      rowsData = rows
        .map((row) =>
          [row.company, row.datetime, row.discount, row.type].join(","),
        )
        .join("\n");
      fileName = "client_pricing.csv";
    }

    if (activeTab === "enterprise") {
      headers = enterpriseColumns.map((col) => col.label).join(",") + "\n";

      rowsData = enterpriseRows
        .map((row) =>
          [row.name, row.Email, row.Mobile, row.api, row.usage].join(","),
        )
        .join("\n");
      fileName = "enterprise_requests.csv";
    }

    if (!rowsData) return;

    const blob = new Blob([headers + rowsData], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ===================== LOAD ORGS & MASTERS ===================== */
  useEffect(() => {
    apiGet("user", "/organization/dropdown").then((res) => {
      if (res?.status === "success") setOrgList(res.data);
    });
  }, []);

  useEffect(() => {
    if (!viewOrgId || !orgList.length) return;
    const org = orgList.find((o) => o.id === viewOrgId) || null;
    setSelectedOrg(org);
  }, [viewOrgId, orgList]);

  useEffect(() => {
    apiGet("catalog", "/apimaster/categories-with-apis?type=1").then((res) => {
      if (res?.status === "success") {
        const mapped = res.data.map((cat) => ({
          categoryId: cat.categoryId,
          categoryName: cat.categoryName,
          apis: cat.apis.map((api) => ({
            apiMasterId: api.id,
            name: api.name,
            default: api.add_credits,
          })),
        }));

        setMasterCategories(mapped);
        setCategoryData(
          mapped.map((cat) => ({
            ...cat,
            categoryDiscount: "",
            manual: false,
            apis: cat.apis.map((api) => ({
              ...api,
              customize: "",
              customized: false,
            })),
          })),
        );
      }
    });
  }, []);

  const resetCustomPricingForm = () => {
    setSelectedOrg(null);
    setGlobalDiscount("");
    setOrgError("");
    setOpenCategory(null);
    setCategoryData(
      masterCategories.map((cat) => ({
        ...cat,
        categoryDiscount: "",
        manual: false,
        apis: cat.apis.map((api) => ({
          ...api,
          customize: "",
          customized: false,
        })),
      })),
    );
  };

  const handleSavePricing = async () => {
    if (isSubmitting) return;

    if (!selectedOrg || !selectedOrg.id) {
      setOrgError("This field is required");
      return;
    }

    setIsSubmitting(true);
    setOrgError("");

    try {
      const payload = {
        orgId: Number(selectedOrg.id),
        companyName: selectedOrg.companyName,
        discount: Number(globalDiscount),
        request_category: categoryData.map((cat) => ({
          categoryId: cat.categoryId,
          categoryDiscount: Number(cat.categoryDiscount || 0),
          request_api: cat.apis.map((api) => ({
            apiMasterId: api.apiMasterId,
            default: Number(api.default || 0),
            customize: Number(api.customize || 0),
          })),
        })),
      };

      const apiUrl =
        pricingMode === "edit"
          ? "/pricing/client/update"
          : "/pricing/client/insert";
      const res = await apiPost("catalog", apiUrl, payload);

      if (res?.status === "success") {
        showNotification(res.message, "success");
        setShowCustomPricing(false);
        fetchPricingData();
      } else {
        showNotification(res?.message || "Failed", "error");
      }
    } catch (err) {
      showNotification(
        err?.response?.data?.message || "Something went wrong",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    if (activeTab === "client") fetchPricingData();
    if (activeTab === "enterprise") fetchEnterprisePricing();
  }, [activeTab, page, limit, appliedSearch, enterprisePage, enterpriseLimit]);

  useEffect(() => {
    resetFilters();
  }, [activeTab]);

  const isSaveDisabled = () => {
    if (isSubmitting) return true;
    if (!selectedOrg || !selectedOrg.id) return true;

    const hasAnyChange =
      globalDiscount !== "" ||
      categoryData.some(
        (cat) =>
          cat.categoryDiscount !== "" ||
          cat.apis.some((api) => api.customize !== ""),
      );

    if (!hasAnyChange) return true;
    return false;
  };

  /* ===================== JSX ===================== */
  return (
    <div className={styles["admin-pricing__page"]}>
      {notification && (
        <div className={styles["admin-pricing__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-pricing__header"]}>
        <div className={styles["admin-pricing__icon-wrapper"]}>
          <CircleDollarSign size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-pricing__title-group"]}>
          <h2 className={styles["admin-pricing__title"]}>Pricing</h2>
          <p className={styles["admin-pricing__subtitle"]}>
            Manage client & enterprise pricing
          </p>
        </div>
      </div>

      <div className={styles["admin-pricing__card"]}>
        {/* TABS & SEARCH */}
        <div className={styles["admin-pricing__controls-row"]}>
          <div className={styles["admin-pricing__tabs"]}>
            <button
              className={`${styles["admin-pricing__tab"]} ${
                activeTab === "client"
                  ? styles["admin-pricing__tab--active"]
                  : ""
              }`}
              onClick={() => setActiveTab("client")}
            >
              Client Pricing
            </button>
            <button
              className={`${styles["admin-pricing__tab"]} ${
                activeTab === "enterprise"
                  ? styles["admin-pricing__tab--active"]
                  : ""
              }`}
              onClick={() => setActiveTab("enterprise")}
            >
              Enterprise Requests
            </button>
          </div>

          <div className={styles["admin-pricing__search-box"]}>
            <Input
              type="search"
              placeholder={
                activeTab === "client"
                  ? "Search Company Name..."
                  : "Search Full Name..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedSearch(search);
                  activeTab === "client" ? setPage(1) : setEnterprisePage(1);
                }
              }}
            />
            <Button
              text="Search"
              onClick={() => {
                setAppliedSearch(search);
                activeTab === "client" ? setPage(1) : setEnterprisePage(1);
              }}
            />
          </div>
        </div>

        {/* ACTION BAR */}
        <div className={styles["admin-pricing__action-bar"]}>
          <div className={styles["admin-pricing__showing-text"]}>
            Showing{" "}
            {activeTab === "client" ? rows.length : enterpriseRows.length} of{" "}
            {activeTab === "client"
              ? pagination.total
              : enterprisePagination.total}
          </div>

          <div className={styles["admin-pricing__action-buttons"]}>
            {canDo("export") && (
              <button
                className={`${styles["admin-pricing__btn"]} ${styles["admin-pricing__btn--outline"]}`}
                onClick={handleDownloadCSV}
              >
                <Download size={16} /> Download
              </button>
            )}

            {activeTab === "client" && canDo("create") && (
              <button
                className={`${styles["admin-pricing__btn"]} ${styles["admin-pricing__btn--primary"]}`}
                onClick={openAddPricing}
              >
                <Plus size={16} /> Add Custom Pricing
              </button>
            )}
          </div>
        </div>

        {/* TABLE: CLIENT */}
        {activeTab === "client" &&
          (loading ? (
            <TableSkeleton columns={5} rows={4} />
          ) : (
            <APITable
              columns={clientColumns}
              data={rows}
              renderCell={(row, key) => {
                if (key === "action") {
                  return (
                    <div className={styles["admin-pricing__action-cell"]}>
                      <div
                        className={styles["admin-pricing__icon-btn"]}
                        onClick={() => handleViewPricing(Number(row.org_id))}
                        title="View Pricing"
                      >
                        <Eye size={18} strokeWidth={2.5} />
                      </div>
                      {canDo("update") && (
                        <div
                          className={styles["admin-pricing__icon-btn"]}
                          onClick={() => handleEditPricing(Number(row.org_id))}
                          title="Edit Pricing"
                        >
                          <Edit2 size={18} strokeWidth={2.5} />
                        </div>
                      )}
                    </div>
                  );
                }
                return row[key];
              }}
            />
          ))}

        {/* TABLE: ENTERPRISE */}
        {activeTab === "enterprise" &&
          (enterpriseLoading ? (
            <TableSkeleton columns={5} rows={4} />
          ) : (
            <APITable columns={enterpriseColumns} data={enterpriseRows} />
          ))}

        {/* PAGINATION */}
        {activeTab === "client" && (
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages}
            rowsPerPage={limit}
            onPageChange={setPage}
            onRowsPerPageChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        )}
        {activeTab === "enterprise" && (
          <Pagination
            currentPage={enterprisePage}
            totalPages={enterprisePagination.totalPages}
            rowsPerPage={enterpriseLimit}
            onPageChange={setEnterprisePage}
            onRowsPerPageChange={(l) => {
              setEnterpriseLimit(l);
              setEnterprisePage(1);
            }}
          />
        )}
      </div>

      {/* CUSTOM PRICING MODAL */}
      {showCustomPricing && (
        <Modal
          title={
            isViewMode
              ? "View Pricing"
              : pricingMode === "edit"
                ? "Edit Custom Pricing"
                : "Add Custom Pricing"
          }
          onClose={() => {
            setShowCustomPricing(false);
            setOrgError("");
          }}
        >
          <div className={styles["admin-pricing__modal-form"]}>
            <Input
              label="Select Customer"
              type="select"
              value={selectedOrg?.id || ""}
              onChange={(e) => {
                const org = orgList.find(
                  (o) => o.id === Number(e.target.value),
                );
                setSelectedOrg(org);
                setOrgError("");
              }}
              options={orgList.map((org) => ({
                label: org.companyName,
                value: org.id,
              }))}
              error={orgError}
              required
              disabled={pricingMode === "edit" || pricingMode === "view"}
            />

            <div className={styles["admin-pricing__global-discount"]}>
              <div className={styles["admin-pricing__global-text"]}>
                <h6>Global Discount</h6>
                <p>Apply a discount to all APIs across all categories</p>
              </div>
              <div className={styles["admin-pricing__discount-input"]}>
                <input
                  type="number"
                  placeholder="0"
                  value={globalDiscount}
                  onChange={(e) =>
                    handleGlobalDiscountChange(safeNumber(e.target.value))
                  }
                  disabled={isViewMode}
                />
                <span
                  className={styles["admin-pricing__discount-input"].percent}
                >
                  %
                </span>
              </div>
            </div>

            <div>
              <div style={{ marginBottom: "12px" }}>
                <h6 style={{ fontSize: "15px", fontWeight: 700, margin: 0 }}>
                  API Categories
                </h6>
                <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                  Category discounts override global discount
                </p>
              </div>

              <div className={styles["admin-pricing__categories"]}>
                {categoryData.map((category) => (
                  <div
                    className={styles["admin-pricing__category-block"]}
                    key={category.categoryId}
                  >
                    <div
                      className={styles["admin-pricing__category-header"]}
                      onClick={() =>
                        setOpenCategory(
                          openCategory === category.categoryId
                            ? null
                            : category.categoryId,
                        )
                      }
                    >
                      <span className={styles["admin-pricing__category-title"]}>
                        {category.categoryName}
                      </span>

                      <div
                        className={styles["admin-pricing__category-right"]}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className={styles["admin-pricing__discount-input"]}
                          style={{ padding: "2px 8px 2px 2px" }}
                        >
                          <input
                            type="number"
                            placeholder="0"
                            value={category.categoryDiscount}
                            onChange={(e) =>
                              handleCategoryDiscountChange(
                                category.categoryId,
                                safeNumber(e.target.value),
                              )
                            }
                            disabled={isViewMode}
                          />
                          <span
                            className={
                              styles["admin-pricing__discount-input"].percent
                            }
                          >
                            %
                          </span>
                        </div>
                        <ChevronDown
                          size={18}
                          className={`${styles["admin-pricing__chevron"]} ${
                            openCategory === category.categoryId
                              ? styles["admin-pricing__chevron--open"]
                              : ""
                          }`}
                        />
                      </div>
                    </div>

                    <Collapse in={openCategory === category.categoryId}>
                      <div>
                        <div className={styles["admin-pricing__api-table"]}>
                          <div
                            className={`${styles["admin-pricing__api-row"]} ${styles["admin-pricing__api-row--head"]}`}
                          >
                            <span>API Name</span>
                            <span>Default Credits</span>
                            <span>Discounted Credits</span>
                          </div>

                          {category.apis.map((api) => (
                            <div
                              className={styles["admin-pricing__api-row"]}
                              key={api.apiMasterId}
                            >
                              <span>{api.name}</span>
                              <span>{api.default}</span>
                              <input
                                type="number"
                                placeholder="0"
                                value={api.customize}
                                onChange={(e) =>
                                  handleApiCustomizeChange(
                                    category.categoryId,
                                    api.apiMasterId,
                                    safeNumber(e.target.value),
                                  )
                                }
                                disabled={isViewMode}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </Collapse>
                  </div>
                ))}
              </div>
            </div>

            {/* MODAL ACTIONS */}
            <div className={styles["admin-pricing__modal-actions"]}>
              {pricingMode !== "view" && (
                <>
                  <button
                    className={`${styles["admin-pricing__btn"]} ${styles["admin-pricing__btn--outline"]}`}
                    onClick={resetCustomPricingForm}
                    disabled={isSubmitting}
                  >
                    <RotateCcw size={16} /> Reset
                  </button>
                  <button
                    className={`${styles["admin-pricing__btn"]} ${styles["admin-pricing__btn--secondary"]}`}
                    onClick={() => {
                      setShowCustomPricing(false);
                      setOrgError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className={`${styles["admin-pricing__btn"]} ${styles["admin-pricing__btn--primary"]}`}
                    onClick={handleSavePricing}
                    disabled={isSaveDisabled()}
                  >
                    <Save size={16} /> {getSaveButtonText()}
                  </button>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminPricing;
